import { useBoolean } from 'usehooks-ts';

import { adultPepForMinorRequest } from '@/api/operation';
import { addAnswer } from '@/api/pep';

import { useAppSelector } from '@/hooks/redux';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectOperationUser } from '@/redux/operation/selectors';

import { PEPAnswer, PEPQuestion } from '@/types/api/pep';

import { useMutationWithUserData } from './useMutationWithUserData';

export type PepQuestionTarget = 'self' | 'minor';

type PepQuestionPayload = Partial<PEPAnswer> & Pick<PEPAnswer, 'positive'>;

type UsePepQuestionArgs = {
  onAnswered: () => void;
  question?: PEPQuestion;
  target?: PepQuestionTarget;
};

export function usePepQuestion({
  onAnswered,
  question,
  target = 'self',
}: UsePepQuestionArgs) {
  const user = useAppSelector(selectLoggedInUser);
  const minorUser = useAppSelector(selectOperationUser);
  const { value: isRefreshing, setValue: setRefreshing } = useBoolean(false);
  const { mutate, isPending, error } = useMutationWithUserData({
    mutationFn: async (payload: PepQuestionPayload) => {
      if (target === 'minor') {
        const operationId = minorUser?.operationId;
        const minorClientId = minorUser?.id;

        if (!operationId || minorClientId === undefined) {
          throw new Error('Operation or minor holder not found');
        }

        return adultPepForMinorRequest(operationId, minorClientId, {
          questionId: question?.id ?? '',
          ...payload,
        });
      }

      return addAnswer({
        answer: {
          questionId: question?.id ?? '',
          ...payload,
        },
        previousAnswers: user?.lastAnswer?.answers || [],
        answerId: user?.answerId,
      });
    },
    onFinished: onAnswered,
  });

  const answerYes = (payload?: Partial<PEPAnswer>) => {
    if (user) {
      setRefreshing(true);
      mutate({ positive: true, ...payload });
    }
  };

  const answerNo = (payload?: Partial<PEPAnswer>) => {
    if (user) {
      setRefreshing(true);
      mutate({ positive: false, ...payload });
    }
  };

  return {
    answerYes,
    answerNo,
    error,
    isPending: isRefreshing || isPending,
  };
}
